/**
 * Bungie's game database ("manifest"), table by table: which tables the site uses and the slimmed-down shape each is
 * kept in. The raw tables are big (the full item table is 199 MB of text), so each is cut down to the fields the
 * pages read before it is stored in the browser. Shared by the page and the download worker.
 */

export interface Display { name: string; description: string; icon: string }

/** An item, from Bungie's lite item table (every weapon, armour piece, currency, emblem...). */
export interface ItemDef {
  name: string;
  icon: string;
  desc: string;
  type: string;        // "Fusion Rifle"
  tierName: string;    // "Legendary"
  tier: number;        // 6 exotic, 5 legendary, 4 rare, 3 common, 2 basic
  bucket: number;
  itemType: number;
  subType: number;
  classType: number;   // 0 Titan, 1 Hunter, 2 Warlock, 3 any
  damage: number;      // 1 kinetic, 2 arc, 3 solar, 4 void, 6 stasis, 7 strand
  ammo: number;        // 1 primary, 2 special, 3 heavy
  watermark: string;
  banner: string;      // an emblem's wide banner (secondarySpecial)
  stack: number;
  categories: number[];
  collectible: number;
  iconHash?: number;   // the layered picture (DestinyIconDefinition): what DIM draws on its squares
}

/** An item's layered picture, as DIM draws it: the item cut out (fg) over the rarity's colour, the season badge (badge). */
export interface IconDef { fg: string; badge?: string }

/**
 * From Bungie's full item table, only what the Item Manager's card needs (DIM keeps the whole table; this is a few MB):
 * cats, where a weapon or armour piece keeps its frame, perks and mods (socket category hash, socket indexes); stats,
 * what a perk or mod always adds ([stat hash, value]).
 */
export interface SlimItem { cats?: [number, number[]][]; stats?: [number, number][]; breaker?: number }   // breaker: the champion it stuns (DestinyBreakerTypeDefinition)

// Which champion a weapon stuns, worked out as DIM does (DestinyItemManager/DIM, MIT licence): the item's own breaker,
// else one a plugged perk or mod grants (its own breakerTypeHash, or one of these game effects), and DIM's list of
// items with a hidden one has the last word.
const STAGGER = 3178805705, DISRUPTION = 2611060930, PIERCING = 485622768;
/** Game effects (sandbox perks) that grant a breaker: DIM's breakerTypeByPerkHash (src/app/search/d2-known-values.ts). */
export const BREAKER_BY_PERK: Record<number, number> = {
  2917776374: STAGGER, 164592134: STAGGER, 1134361550: STAGGER, 2084279142: STAGGER, 2612240126: STAGGER,
  472686235: DISRUPTION, 15807694: DISRUPTION, 656925452: DISRUPTION, 1478638730: DISRUPTION, 2026681315: DISRUPTION, 3033353825: DISRUPTION, 4114351468: DISRUPTION,
  3469621377: PIERCING, 4000621756: PIERCING, 549294277: PIERCING, 1235808583: PIERCING, 1435986861: PIERCING, 1839099519: PIERCING, 3437929382: PIERCING, 3656570035: PIERCING,
};
/** Items with a breaker the game data leaves out: DIM's src/data/d2/extended-breaker.json. */
export const EXTENDED_BREAKER: Record<number, number> = {
  17096506: STAGGER, 89619507: STAGGER, 449318888: PIERCING, 503025963: PIERCING, 511888814: DISRUPTION, 593808239: PIERCING, 1003391927: PIERCING,
  1047932517: DISRUPTION, 1203306857: STAGGER, 1307844890: PIERCING, 1351987111: STAGGER, 1443166262: PIERCING, 1685137410: STAGGER, 1734844650: STAGGER,
  1955548646: PIERCING, 2110100341: STAGGER, 2415768376: STAGGER, 2474293653: PIERCING, 2687811401: DISRUPTION, 2694576561: DISRUPTION, 3118061004: STAGGER,
  3487253372: PIERCING, 3598649636: PIERCING, 3661051060: PIERCING, 4029987901: DISRUPTION, 4095189811: PIERCING, 4268030601: PIERCING,
};

/** Tables too big to read in one piece (the full item table is 199 MB of text): read entry by entry as they arrive. */
export const STREAMED = new Set<string>(['DestinyInventoryItemDefinition']);

/** Bump when the slimmed shape of a table changes: browsers that kept the old shape download it again. */
export const TABLE_FORMAT = 6;   // 6: the slim item table marks perks and mods that grant a breaker; 5: items keep their layered picture (iconHash); 4: vendors keep their subtitle and small icon

export type Table<T> = Record<string, T>;

const dp = (d: any): Display => ({
  name: d?.displayProperties?.name ?? '',
  description: d?.displayProperties?.description ?? '',
  icon: d?.displayProperties?.icon ?? '',
});

const pick = (keys: string[]) => (d: any) => {
  const o: any = { displayProperties: dp(d) };
  for (const k of keys) if (d[k] !== undefined) o[k] = d[k];
  return o;
};

/** How each table is slimmed (tables not listed are kept whole: they are small). An entry slimmed to undefined is left out. */
export const PRUNE: Record<string, (d: any) => any> = {
  DestinyInventoryItemDefinition: (d): SlimItem | undefined => {
    const out: SlimItem = {};
    if (d.itemType === 2 || d.itemType === 3) {
      const cats = (d.sockets?.socketCategories ?? []).map((c: any): [number, number[]] => [c.socketCategoryHash, c.socketIndexes ?? []]);
      if (cats.length) out.cats = cats;
      if (d.breakerTypeHash) out.breaker = d.breakerTypeHash;
    }
    if (d.plug) {
      const stats = (d.investmentStats ?? []).filter((s: any) => s.value && !s.isConditionallyActive).map((s: any): [number, number] => [s.statTypeHash, s.value]);
      if (stats.length) out.stats = stats;
      // a perk or mod that grants a breaker, itself or through one of its game effects
      const breaker = d.breakerTypeHash || (d.perks ?? []).map((p: any) => BREAKER_BY_PERK[p.perkHash]).find(Boolean);
      if (breaker) out.breaker = breaker;
    }
    return out.cats || out.stats || out.breaker ? out : undefined;
  },
  DestinyInventoryItemLiteDefinition: (d): ItemDef => ({
    name: d.displayProperties?.name ?? '',
    icon: d.displayProperties?.icon ?? '',
    desc: d.displayProperties?.description ?? '',
    type: d.itemTypeDisplayName ?? '',
    tierName: d.inventory?.tierTypeName ?? '',
    tier: d.inventory?.tierType ?? 0,
    bucket: d.inventory?.bucketTypeHash ?? 0,
    itemType: d.itemType ?? 0,
    subType: d.itemSubType ?? 0,
    classType: d.classType ?? 3,
    damage: d.defaultDamageType ?? 0,
    ammo: d.equippingBlock?.ammoType ?? 0,
    watermark: d.iconWatermark ?? '',
    banner: d.secondarySpecial ?? '',
    stack: d.inventory?.maxStackSize ?? 1,
    categories: d.itemCategoryHashes ?? [],
    collectible: d.collectibleHash ?? 0,
    iconHash: d.displayProperties?.iconHash || undefined,
  }),
  DestinyIconDefinition: (d): IconDef | undefined => (d.foreground ? { fg: d.foreground, badge: d.secondaryBackground || undefined } : undefined),
  DestinyActivityDefinition: pick(['pgcrImage', 'activityTypeHash', 'matchmaking', 'destinationHash', 'placeHash', 'activityLightLevel', 'rewards', 'directActivityModeType', 'isPvP', 'originalDisplayProperties', 'selectionScreenDisplayProperties', 'releaseIcon', 'tier']),
  DestinyPresentationNodeDefinition: pick(['originalIcon', 'children', 'completionRecordHash', 'presentationNodeType', 'parentNodeHashes']),
  DestinyRecordDefinition: pick(['objectiveHashes', 'intervalInfo', 'completionInfo', 'titleInfo', 'forTitleGilding', 'loreHash', 'recordValueStyle', 'parentNodeHashes', 'rewardItems']),
  DestinyObjectiveDefinition: (d) => ({ progressDescription: d.progressDescription ?? '', completionValue: d.completionValue ?? 0, valueStyle: d.valueStyle, inProgressValueStyle: d.inProgressValueStyle, displayProperties: dp(d) }),
  DestinyCollectibleDefinition: pick(['sourceString', 'itemHash', 'parentNodeHashes']),
  DestinyMetricDefinition: pick(['trackingObjectiveHash', 'parentNodeHashes']),
  DestinyProgressionDefinition: pick(['rewardItems', 'steps', 'scope']),
  DestinyLoreDefinition: pick(['subtitle']),
  DestinySandboxPerkDefinition: pick([]),
  // a vendor's subtitle (Titan Vanguard, Crucible Handler...) says it is a person you can visit: Vendors lists only those
  DestinyVendorDefinition: (d) => ({ ...pick(['displayCategories', 'groups', 'locations', 'vendorBanner', 'vendorProgressionType', 'enabled', 'visible', 'factionHash', 'vendorSubcategoryIdentifier'])(d),
    displayProperties: { ...dp(d), subtitle: d?.displayProperties?.subtitle ?? '', smallTransparentIcon: d?.displayProperties?.smallTransparentIcon ?? '' } }),
};

/** The tables the site reads. */
export type TableName =
  | 'DestinyInventoryItemLiteDefinition'
  | 'DestinyInventoryItemDefinition'
  | 'DestinyBreakerTypeDefinition'
  | 'DestinyIconDefinition'
  | 'DestinyActivityDefinition'
  | 'DestinyActivityTypeDefinition'
  | 'DestinyActivityModifierDefinition'
  | 'DestinyPresentationNodeDefinition'
  | 'DestinyRecordDefinition'
  | 'DestinyObjectiveDefinition'
  | 'DestinyCollectibleDefinition'
  | 'DestinyMetricDefinition'
  | 'DestinyProgressionDefinition'
  | 'DestinyMilestoneDefinition'
  | 'DestinySeasonDefinition'
  | 'DestinySeasonPassDefinition'
  | 'DestinyEventCardDefinition'
  | 'DestinyGuardianRankDefinition'
  | 'DestinyDestinationDefinition'
  | 'DestinyPlaceDefinition'
  | 'DestinyStatDefinition'
  | 'DestinyInventoryBucketDefinition'
  | 'DestinyDamageTypeDefinition'
  | 'DestinyLoreDefinition'
  | 'DestinyVendorDefinition'
  | 'DestinyFactionDefinition'
  | 'DestinyActivityModeDefinition'
  | 'DestinyLoadoutNameDefinition'
  | 'DestinyLoadoutIconDefinition'
  | 'DestinyLoadoutColorDefinition'
  | 'DestinyEquipableItemSetDefinition'
  | 'DestinySandboxPerkDefinition';
